import { Controller } from "@hotwired/stimulus"

// Modal that lazy-loads the New / Edit Report Folder form via XHR and
// submits in-place. On 422 the slot re-renders the form (with errors);
// on success we close + reload so the sidebar/manage page reflect the
// new state.
//
// Usage: place the controller on a wrapper that contains both the
// triggers and the modal markup. Triggers carry an href (so standalone
// nav still works without JS) plus:
//
//   data-action="click->folder-form-modal#open"
//   data-url="<%= new_report_folder_path %>"
//   data-title="New folder"
//
// The modal markup needs `[data-folder-form-modal-target="modal"]`,
// `slot`, and `title`. See `report_folders/_modal.html.erb`.
export default class extends Controller {
  static targets = ["modal", "slot", "title"]

  connect() {
    this._onSubmit = this._onSubmit.bind(this)
    this._onKeydown = this._onKeydown.bind(this)
    this.slotTarget.addEventListener("submit", this._onSubmit)
  }

  disconnect() {
    this.slotTarget.removeEventListener("submit", this._onSubmit)
    document.removeEventListener("keydown", this._onKeydown)
  }

  open(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return
    event.preventDefault()
    const trigger = event.currentTarget
    const url = trigger.dataset.url
    if (!url) return
    this._setTitle(trigger.dataset.title || "Folder")
    this._show()
    this._fetchInto(url)
  }

  close(event) {
    if (event) event.preventDefault()
    this._hide()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async _onSubmit(event) {
    const form = event.target.closest("form")
    if (!form) return
    event.preventDefault()

    const method = (form.method || "post").toUpperCase()
    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    this._showLoading()

    try {
      const res = await fetch(form.action, {
        method,
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrf || ""
        },
        body: new FormData(form),
        credentials: "same-origin",
        redirect: "follow"
      })
      if (res.status === 422) {
        const html = await res.text()
        this.slotTarget.innerHTML = html
        return
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      // Success — server redirected to the canonical URL and the XHR
      // followed it. Close + reload so the sidebar / manage list reflect
      // the new state.
      this._hide()
      window.location.reload()
    } catch (err) {
      console.error("[folder-form-modal] submit failed", err)
      this._showError()
    }
  }

  async _fetchInto(url) {
    this._showLoading()
    try {
      const res = await fetch(url, {
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin"
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const html = await res.text()
      this.slotTarget.innerHTML = html
      this._focusFirstField()
    } catch (err) {
      console.error("[folder-form-modal] fetch failed", err)
      this._showError()
    }
  }

  _focusFirstField() {
    const field = this.slotTarget.querySelector("input[type='text'], input[type='email'], textarea, select")
    if (field) field.focus()
  }

  _show() {
    this.modalTarget.classList.remove("hidden")
    document.addEventListener("keydown", this._onKeydown)
  }

  _hide() {
    this.modalTarget.classList.add("hidden")
    this.slotTarget.innerHTML = ""
    document.removeEventListener("keydown", this._onKeydown)
  }

  _onKeydown(event) {
    if (event.key === "Escape") this._hide()
  }

  _setTitle(title) {
    if (this.hasTitleTarget) this.titleTarget.textContent = title || ""
  }

  _showLoading() {
    this.slotTarget.innerHTML =
      `<div class="py-10 text-center text-[13px] text-gray-500">Loading…</div>`
  }

  _showError() {
    this.slotTarget.innerHTML =
      `<div class="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">Something went wrong. Refresh and try again.</div>`
  }
}
